/**
 * The office run pages (fieldiq-api#1338):
 *
 *   /wireline-slickline/runs/:id               — one run, by kind; edit and
 *                                                delete while its sheet is a draft
 *   /wireline-slickline/sheets/:id/runs/new    — add a run to a draft sheet
 *
 * On create the API runs the rig-up rule: with the tenant's setting off, a
 * first run with no passing rig-up test saves and comes back with the
 * rig_up_test_missing WARNING; with it on, the run is REFUSED (409
 * rig_up_test_required) and the refusal is shown on the form. Misfire
 * mismatches are warnings either way.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Download, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useWlLines, useWlMeta, useWlToolStrings } from '../../hooks/useWirelineSlickline';
import { useAddWlRun, useDeleteWlRun, useUpdateWlRun, useWlRun, useWlSheet } from '../../hooks/useWlJobs';
import { claimBlobTab } from '../../lib/blob-tab';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { openWlRunPdf } from '../../lib/wireline-slickline-reports';
import { canWriteWlSheets, wlDay, wlJobRefusal, wlRigUpRefusal } from '../../lib/wireline-slickline-jobs';
import { useBackToList } from '../downhole/recordPage';
import { RunForm, RunReadout, RunWarnings } from './RunParts';
import type { WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { wlRunPath } from './SheetScreen';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WL_SECONDARY, WlDialog, WlEmpty, WlFormError, WlLoading, WlPanel } from './WlBits';

const sheetPath = (id: string) => `/wireline-slickline/sheets/${id}`;

function useRunOptions() {
  const lines = useWlLines();
  const strings = useWlToolStrings();
  return {
    lines: (lines.data?.data ?? []).map((l) => ({ id: l.id, name: `${l.lineNumber}${l.lineTypeLabel ? ` · ${l.lineTypeLabel}` : ''}` })),
    toolStrings: (strings.data?.data ?? []).map((t) => ({ id: t.id, name: t.name })),
  };
}

/** A run create refusal: the rig-up 409 reads as the API's sentence, anything else through wlJobRefusal. */
export const wlRunRefusal = (err: unknown, fallback: string) => wlRigUpRefusal(err) ?? wlJobRefusal(err, fallback);

export function WlNewRunScreen() {
  const { id: sheetId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sheet = useWlSheet(sheetId);
  const meta = useWlMeta();
  const opts = useRunOptions();
  const add = useAddWlRun();
  const [error, setError] = useState<string | null>(null);
  const [rigUp, setRigUp] = useState<string | null>(null);
  const s = sheet.data?.data ?? null;
  const failure = queryLoadFailure(sheet);
  const writable = !!s && s.status === 'draft' && !s.locked && canWriteWlSheets(user?.role);

  return (
    <div data-testid="wl-new-run-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="Add a run" onBack={() => navigate(sheetPath(sheetId ?? ''))} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {sheet.isLoading ? (
            <WlLoading label="Loading day sheet" />
          ) : failure || !s ? (
            <WlPanel eyebrow="Day sheet" title="Day sheet">
              <LoadFailureNotice error={queryFailureError(sheet)} kind={failure ?? 'unreachable'} subject="this day sheet" />
            </WlPanel>
          ) : !writable ? (
            <WlPanel eyebrow="Day sheet" title="Day sheet">
              <WlEmpty>Runs are added to a draft sheet only.</WlEmpty>
            </WlPanel>
          ) : (
            <>
              <p className="text-sm text-fieldiq-muted">
                {s.jobNumber} · {s.unitNumber} · {wlDay(s.workDate)} · sheet {s.startTime ?? '—'}–{s.endTime ?? '—'}
              </p>
              {rigUp && (
                <div role="alert" data-testid="wl-rig-up-refused" className="space-y-1 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
                  <p className="font-semibold">{rigUp}</p>
                  <p>
                    The company requires it.{' '}
                    <Link to={sheetPath(s.id)} className="font-semibold text-fi-amber underline">
                      Link a rig-up test on the day sheet
                    </Link>
                    , then add the run.
                  </p>
                </div>
              )}
              <RunForm
                run={null}
                catalog={meta.data?.data ?? null}
                meta={sheet.data?.meta ?? meta.data?.meta ?? null}
                lines={opts.lines}
                toolStrings={opts.toolStrings}
                pending={add.isPending}
                error={error}
                submitLabel="Add run"
                onCancel={() => navigate(sheetPath(s.id))}
                onSubmit={(body) => {
                  setError(null);
                  setRigUp(null);
                  add.mutate(
                    { sheetId: s.id, body },
                    {
                      onSuccess: (res) => {
                        if (res.warnings.length) toast.warning(`Run saved with ${res.warnings.length} warning${res.warnings.length === 1 ? '' : 's'}`);
                        else toast.success('Run added');
                        navigate(res.data?.id ? wlRunPath(res.data.id) : sheetPath(s.id), { state: { warnings: res.warnings } });
                      },
                      onError: (err) => {
                        // The rig-up 409 is its own refusal, with the way to clear it; anything else is the form's error line.
                        const r = wlRigUpRefusal(err);
                        setRigUp(r);
                        setError(r ? null : wlJobRefusal(err, 'The run could not be added.'));
                      },
                    },
                  );
                }}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default function WlRunScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  // The create response's warnings (the rig-up one is the sheet's, not the run's): shown once, on arrival.
  const created = ((location.state as { warnings?: WlWarningEntry[] } | null)?.warnings ?? []).filter((w) => w.code !== 'misfire_count_mismatch');
  const query = useWlRun(id);
  const run = query.data?.data ?? null;
  const sheet = useWlSheet(run?.sheetId);
  const meta = useWlMeta();
  const back = useBackToList(run ? sheetPath(run.sheetId) : '/wireline-slickline/jobs');
  const s = sheet.data?.data ?? null;
  const writable = !!s && s.status === 'draft' && !s.locked && canWriteWlSheets(user?.role);
  const [editing, setEditing] = useState(false);
  const update = useUpdateWlRun();
  const del = useDeleteWlRun();
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const opts = useRunOptions();
  const failure = queryLoadFailure(query);
  const [pdfError, setPdfError] = useState<string | null>(null);
  /** WL-3 (api#1339): this run's run log as a PDF, any reader's. The tab is claimed before the await. */
  const runPdf = async () => {
    setPdfError(null);
    const err = await openWlRunPdf(run!.id, claimBlobTab());
    if (err) setPdfError(err);
  };

  return (
    <div data-testid="wl-run-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={run ? `Run ${run.runNumber} · ${run.runKindLabel ?? run.runKind}` : query.isLoading ? 'Run' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          run ? (
            <div className="flex flex-wrap gap-2">
              {writable && !editing && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setError(null); setEditing(true); }}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit run
                </button>
              )}
              <button type="button" className={WL_BAND_SECONDARY} onClick={runPdf} data-testid="wl-run-pdf">
                <Download className="size-4" aria-hidden="true" />
                Run PDF
              </button>
              {writable && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading run" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !run) ? (
            <WlPanel eyebrow="Run" title="Run">
              <WlEmpty>This run was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !run ? (
            <WlPanel eyebrow="Run" title="Run">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this run" />
            </WlPanel>
          ) : editing && writable ? (
            <RunForm
              key={run.updatedAt ?? run.id}
              run={run}
              catalog={meta.data?.data ?? null}
              meta={query.data?.meta ?? null}
              lines={opts.lines}
              toolStrings={opts.toolStrings}
              pending={update.isPending}
              error={error}
              submitLabel="Save run"
              onCancel={() => setEditing(false)}
              onSubmit={(body) => {
                setError(null);
                update.mutate(
                  { id: run.id, body },
                  {
                    onSuccess: () => {
                      toast.success('Run saved');
                      setEditing(false);
                    },
                    onError: (err) => setError(wlRunRefusal(err, 'The run could not be saved.')),
                  },
                );
              }}
            />
          ) : (
            <>
              <WlFormError message={pdfError} />
              <RunWarnings warnings={created} testId="wl-run-created-warnings" />
              <RunReadout run={run} />
            </>
          )}
        </div>
      </section>
      {run && (
        <WlDialog
          open={deleting}
          title={`Delete run ${run.runNumber}`}
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep it
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(run.id, {
                    onSuccess: () => {
                      toast.success('Run deleted');
                      navigate(sheetPath(run.sheetId), { replace: true });
                    },
                    onError: (err) => setDeleteError(wlJobRefusal(err, 'The run could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete run'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">The run is removed from the draft sheet.</p>
        </WlDialog>
      )}
    </div>
  );
}
